<template>
  <svg
    class="svg-icon"
    :class="[props.to, { loading: props.loading, transition: props.transition }]"
    :style="iconStyle"
    :role="props.label ? 'img' : undefined"
    :aria-label="props.label || undefined"
    :aria-hidden="props.label ? undefined : true"
  >
    <!--
      SvgIcon 会通过 vite-plugin-svg-icons 把 SVG 文件合并成 <symbol>，
      再用 <use> 引用对应 symbol。它适合普通 path、circle、rect、
      linearGradient、mask、clipPath 等原生 SVG 图标。

      以下 SVG 在 symbol/use 链路里可能无法正常渲染：
      1. 依赖 foreignObject 承载 HTML/CSS 的内容，例如 XHTML div + conic-gradient。
      2. Figma 导出的 data-figma-gradient-fill、data-figma-skip-parse 等非标准元数据；
         浏览器不会把这些属性当作真正的 fill 或 stroke。
      3. 依赖独立 SVG 文档上下文的样式、动画、外部资源或脚本。
      4. 多个 SVG 内部 defs 使用相同 id 时，合并到同一个 sprite 后可能互相引用错位。

      如果需要完整保留这类复杂 SVG 的渲染效果，请不要使用 SvgIcon，
      改用 <img src="*.svg">、Vite 的 ?url 资源引用，或在业务组件中直接内联原 SVG。
    -->
    <use v-if="iconName" :href="symbolId" :xlink:href="symbolId" />
  </svg>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import type { CSSProperties } from 'vue';

  interface Props {
    name?: string;
    icon?: string;
    size?: number | string;
    color?: string;
    label?: string;
    to?: 'down' | 'up' | 'left' | '';
    loading?: boolean;
    transition?: boolean;
  }

  const props = defineProps<Props>();
  const iconName = computed(() => props.name ?? props.icon ?? '');
  const symbolId = computed(() => `#icon-${iconName.value}`);

  const iconStyle = computed<CSSProperties>(() => {
    const style: CSSProperties = {};

    if (props.size !== undefined) {
      const size = normalizeSize(props.size);
      style.width = size;
      style.height = size;
    }

    if (props.color) {
      style.color = props.color;
    }

    return style;
  });

  function normalizeSize(size: number | string) {
    return typeof size === 'number' ? `${size}px` : size;
  }
</script>

<style scoped>
  .svg-icon {
    display: inline-block;
    width: 1em;
    height: 1em;
    overflow: hidden;
    color: inherit;
    fill: currentColor;
    flex-shrink: 0;
    line-height: 1;
    vertical-align: -0.125em;
  }

  .svg-icon.up {
    transform: rotate(-90deg);
  }

  .svg-icon.down {
    transform: rotate(90deg);
  }

  .svg-icon.left {
    transform: rotate(180deg);
  }

  .svg-icon.loading {
    animation: svg-icon-loading 1s linear infinite;
  }

  .svg-icon.transition {
    transition: transform 0.2s linear;
  }

  .svg-icon:focus {
    outline: unset;
  }

  @keyframes svg-icon-loading {
    to {
      transform: rotate(360deg);
    }
  }
</style>
